import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { LTText } from '@lambdatestincprivate/lt-components';
import { formatBytes } from '../../format';
import styles from './dock.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ROW_ESTIMATE_PX = 20;
var OVERSCAN = 16;

/**
 * Raw text log (the runner/terminal log): virtualized monospace lines with
 * a line-number gutter — no parsing, exactly what the file says.
 */
export function RawLogViewer(_ref) {
  var log = _ref.log,
    emptyText = _ref.emptyText;
  // Scroll element in STATE, not a ref — see ConsoleLogViewer.
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    scrollEl = _useState2[0],
    setScrollEl = _useState2[1];
  var lines = log.lines;
  var virtualizer = useVirtualizer({
    count: lines.length,
    getScrollElement: function getScrollElement() {
      return scrollEl;
    },
    estimateSize: function estimateSize() {
      return ROW_ESTIMATE_PX;
    },
    overscan: OVERSCAN
  });
  if (log.phase === 'loading') {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      "aria-busy": "true",
      children: Array.from({
        length: 5
      }, function (_, i) {
        return /*#__PURE__*/_jsx("span", {
          className: styles.logSkeletonBar,
          "aria-hidden": "true"
        }, i);
      })
    });
  }
  if (log.phase === 'missing' || log.phase === 'ready' && lines.length === 0) {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      children: /*#__PURE__*/_jsx(LTText, {
        text: emptyText,
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "#8c959f"
      })
    });
  }
  if (log.phase === 'too-large') {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      children: /*#__PURE__*/_jsx(LTText, {
        text: "This log is ".concat(log.totalBytes !== null ? formatBytes(log.totalBytes) : 'too large', " \u2014 too large to display in the viewer."),
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "#8c959f"
      })
    });
  }
  if (log.phase === 'error') {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      children: /*#__PURE__*/_jsx(LTText, {
        text: "Couldn't read this log from the pack.",
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "#8c959f"
      })
    });
  }
  var items = virtualizer.getVirtualItems();
  return /*#__PURE__*/_jsx("div", {
    ref: setScrollEl,
    className: styles.logScroll,
    children: /*#__PURE__*/_jsx("ul", {
      className: styles.logList,
      style: {
        height: virtualizer.getTotalSize()
      },
      children: items.map(function (item) {
        return /*#__PURE__*/_jsxs("li", {
          ref: virtualizer.measureElement,
          "data-index": item.index,
          className: styles.logRow,
          "aria-setsize": lines.length,
          "aria-posinset": item.index + 1,
          style: {
            transform: "translateY(".concat(item.start, "px)")
          },
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.logGutter,
            "aria-hidden": "true",
            children: item.index + 1
          }), /*#__PURE__*/_jsx("span", {
            className: styles.logText,
            children: lines[item.index]
          })]
        }, item.index);
      })
    })
  });
}